import type { BoardDoc } from '../../doc/BoardDoc';
import type { Rect } from '../../doc/geometry';
import type { SheetItem, SheetLayerDef } from '../../doc/schema';
import { enqueueUpload } from '../../sync/uploadQueue';
import { getAssetBlob, storeCanvasAsset } from '../assets/localAssets';
import { clipRect, TILE, unionRect, type FillRegion, type StrokeClip } from './brushes';

/**
 * Live pixels of sketch sheets. A layer being painted is a real canvas (`pxW × pxH`); every
 * other layer is shown from its asset. Strokes change the canvas at once; a debounced commit
 * encodes it to a PNG asset and points the layer at it (synced to other devices). Paint undo
 * is a per-sheet history of the tiles each stroke touched, separate from the board's undo.
 *
 * The store also holds the pixel selection (the lasso's outline on the sheet being painted) and
 * the pixels the move tool has lifted off the layer: they float over it, shown on the scratch
 * canvas, until something else happens, and only then land as one undoable step.
 *
 * One store per open document, shared by both panes of a split view.
 */

export const COMMIT_DELAY_MS = 1500;
const MAX_UNDO_STEPS = 40;
const MAX_UNDO_BYTES = 120 * 1024 * 1024;
/** Retired live layers kept (so switching back is instant and the swap to an <img> is rare). */
const KEEP_RETIRED = 2;

export interface LiveLayer {
  key: string;
  sheetId: string;
  layerId: string;
  canvas: HTMLCanvasElement;
  /** Paint brushes draw here first (same size as the canvas). Allocated for the active layer. */
  scratch: HTMLCanvasElement | null;
  loaded: boolean;
  /** The layer's asset could not be fetched (offline, not cached): painting is refused. */
  unavailable: boolean;
  /** Pixels changed since the last commit. */
  dirty: boolean;
  /** The asset the canvas was loaded from or last committed to (null: empty). */
  assetId: string | null;
  /** No longer the active layer: dropped once its commit lands and it falls out of the LRU. */
  retired: boolean;
  lastUsed: number;
}

/** Pixels picked out with the lasso, in layer pixels. Strokes, fills and the move tool keep to it. */
export interface PixelSelection {
  sheetId: string;
  /** The outline, flat [x, y, ...]. */
  points: number[];
  path: Path2D;
  /** Where the mask sits (whole pixels; may hang over the sheet's edge after a move). */
  bounds: Rect;
  /** The selection as alpha, `bounds` in size. */
  mask: HTMLCanvasElement;
  /** Hard edge: every pixel is either in or out. */
  aliased: boolean;
  /** The mask's pixels, read once they are needed. */
  data: Uint8ClampedArray | null;
}

/** Pixels lifted off a layer by the move tool. */
export interface FloatingPixels {
  live: LiveLayer;
  canvas: HTMLCanvasElement;
  /** Where the pixels are now, and where they were lifted from (top-left, layer pixels). */
  x: number;
  y: number;
  x0: number;
  y0: number;
  at: number;
  backup: (rect: Rect) => void;
  /** What the scratch canvas shows of it at the moment. */
  shown: Rect | null;
}

interface UndoTile {
  x: number;
  y: number;
  data: ImageData;
}

interface UndoEntry {
  layerId: string;
  tiles: Map<string, UndoTile>;
  bytes: number;
  at: number;
}

interface SheetHistory {
  undo: UndoEntry[];
  redo: UndoEntry[];
}

const key = (sheetId: string, layerId: string) => `${sheetId}:${layerId}`;

export class RasterStore {
  private live = new Map<string, LiveLayer>();
  private history = new Map<string, SheetHistory>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private committing = new Map<string, Promise<void>>();
  private listeners = new Set<() => void>();
  private version = 0;
  /** The stroke being recorded, per layer key. */
  private recording = new Map<string, UndoEntry>();
  private sel: PixelSelection | null = null;
  private float: FloatingPixels | null = null;
  private unsubscribe: () => void;

  constructor(readonly board: BoardDoc) {
    // A layer's asset changed under a live canvas (another device, a board undo of a clear or
    // merge, a deleted layer): reload it unless local strokes are waiting to be committed.
    this.unsubscribe = board.index.subscribe(() => {
      for (const l of [...this.live.values()]) {
        const def = this.layerDef(l.sheetId, l.layerId);
        if (!def) {
          this.drop(l);
          continue;
        }
        if (def.assetId !== l.assetId && !l.dirty && !this.committing.has(l.key) && this.float?.live !== l) void this.load(l, def.assetId);
      }
    });
  }

  // ---- subscription (SheetItem bodies and the panels re-render on change) ----------------

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getVersion = (): number => this.version;

  private bump(): void {
    this.version++;
    for (const l of this.listeners) l();
  }

  // ---- lookups ---------------------------------------------------------------------------

  sheet(sheetId: string): SheetItem | null {
    const item = this.board.index.get(sheetId);
    return item && item.type === 'sheet' ? item : null;
  }

  layerDef(sheetId: string, layerId: string): SheetLayerDef | null {
    return this.sheet(sheetId)?.props.layers[layerId] ?? null;
  }

  liveLayer(sheetId: string, layerId: string): LiveLayer | undefined {
    return this.live.get(key(sheetId, layerId));
  }

  /** Every live layer of a sheet (active and retired). */
  liveLayersOf(sheetId: string): LiveLayer[] {
    return [...this.live.values()].filter((l) => l.sheetId === sheetId);
  }

  /** Steps the paint undo/redo of a sheet can take. */
  canUndo(sheetId: string): boolean {
    return this.float?.live.sheetId === sheetId || (this.history.get(sheetId)?.undo.length ?? 0) > 0;
  }

  canRedo(sheetId: string): boolean {
    return (this.history.get(sheetId)?.redo.length ?? 0) > 0;
  }

  /** When the newest paint step of the sheet was made (to compare with the board's undo). */
  lastUndoAt(sheetId: string): number {
    if (this.float?.live.sheetId === sheetId) return this.float.at;
    const h = this.history.get(sheetId);
    return h?.undo[h.undo.length - 1]?.at ?? 0;
  }

  /** When the newest undone paint step was undone (to compare with the board's redo). */
  lastRedoAt(sheetId: string): number {
    const h = this.history.get(sheetId);
    return h?.redo[h.redo.length - 1]?.at ?? 0;
  }

  // ---- activation ------------------------------------------------------------------------

  /**
   * Makes a layer live (loading its asset) and the only active one: other live layers of the
   * board are committed and retired. Resolves once the canvas holds the layer's pixels.
   */
  async activate(sheetId: string, layerId: string): Promise<LiveLayer | null> {
    const sheet = this.sheet(sheetId);
    const def = sheet?.props.layers[layerId];
    if (!sheet || !def) return null;
    const k = key(sheetId, layerId);
    let l = this.live.get(k);
    if (this.sel && this.sel.sheetId !== sheetId) this.sel = null;
    for (const other of this.live.values()) {
      if (other.key !== k && !other.retired) this.retire(other);
    }
    if (!l) {
      const canvas = document.createElement('canvas');
      canvas.width = sheet.props.pxW;
      canvas.height = sheet.props.pxH;
      // the undo backs tiles up with getImageData before every stroke: keep the pixels CPU-side
      canvas.getContext('2d', { willReadFrequently: true });
      l = { key: k, sheetId, layerId, canvas, scratch: null, loaded: false, unavailable: false, dirty: false, assetId: def.assetId, retired: false, lastUsed: Date.now() };
      this.live.set(k, l);
      this.bump();
      await this.load(l, def.assetId);
    } else {
      l.retired = false;
      l.lastUsed = Date.now();
    }
    if (!l.scratch) {
      const scratch = document.createElement('canvas');
      scratch.width = l.canvas.width;
      scratch.height = l.canvas.height;
      l.scratch = scratch;
    }
    this.pruneRetired();
    this.bump();
    return l;
  }

  /** The active (not retired, loaded) live layer of a sheet, if any. */
  active(sheetId: string): LiveLayer | null {
    for (const l of this.live.values()) if (l.sheetId === sheetId && !l.retired) return l;
    return null;
  }

  /** Leaves paint mode on a sheet: commits what is pending and retires its layers. */
  deactivate(sheetId?: string): void {
    if (this.sel && (!sheetId || this.sel.sheetId === sheetId)) this.sel = null;
    for (const l of this.live.values()) {
      if (sheetId && l.sheetId !== sheetId) continue;
      if (!l.retired) this.retire(l);
    }
    this.pruneRetired();
    this.bump();
  }

  private retire(l: LiveLayer): void {
    if (this.float?.live === l) this.dropFloat();
    l.retired = true;
    l.scratch = null;
    if (l.dirty) void this.commit(l);
  }

  /** Drops retired layers beyond the LRU budget (never one with pixels still to commit). */
  private pruneRetired(): void {
    const retired = [...this.live.values()].filter((l) => l.retired && !l.dirty && !this.committing.has(l.key)).sort((a, b) => b.lastUsed - a.lastUsed);
    for (const l of retired.slice(KEEP_RETIRED)) this.drop(l);
  }

  private drop(l: LiveLayer): void {
    if (this.float?.live === l) {
      // the layer is gone: so are the pixels lifted off it
      this.float.canvas.width = 0;
      this.float = null;
      this.recording.delete(l.key);
    }
    const t = this.timers.get(l.key);
    if (t) clearTimeout(t);
    this.timers.delete(l.key);
    this.live.delete(l.key);
    l.canvas.width = 0;
    l.canvas.height = 0;
    this.bump();
  }

  private async load(l: LiveLayer, assetId: string | null): Promise<void> {
    l.loaded = false;
    l.unavailable = false;
    l.assetId = assetId;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, l.canvas.width, l.canvas.height);
    if (assetId) {
      let ok = false;
      const blob = await getAssetBlob(assetId, 'original');
      // the layer may have changed again while the blob was fetched
      if (l.assetId !== assetId || !this.live.has(l.key)) return;
      if (blob) {
        try {
          const bmp = await createImageBitmap(blob);
          if (l.assetId === assetId && this.live.has(l.key)) {
            ctx.drawImage(bmp, 0, 0, l.canvas.width, l.canvas.height);
            ok = true;
          }
          bmp.close();
        } catch (e) {
          console.warn('[paint] layer asset could not be decoded', assetId, e);
        }
      }
      if (!ok) {
        // never let a blank canvas overwrite pixels that only failed to arrive
        l.unavailable = true;
        this.bump();
        return;
      }
    }
    l.loaded = true;
    this.bump();
  }

  // ---- strokes and undo ------------------------------------------------------------------

  /** Starts recording a stroke's undo entry on a layer. */
  beginStroke(l: LiveLayer): (rect: Rect) => void {
    // floating pixels land before anything else touches the layer
    if (this.float) this.dropFloat();
    const entry: UndoEntry = { layerId: l.layerId, tiles: new Map(), bytes: 0, at: Date.now() };
    this.recording.set(l.key, entry);
    return (rect) => this.backupTiles(l, entry, rect);
  }

  private backupTiles(l: LiveLayer, entry: UndoEntry, rect: Rect): void {
    const r = clipRect(rect, l.canvas.width, l.canvas.height);
    if (!r) return;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return;
    const tx0 = Math.floor(r.x / TILE);
    const ty0 = Math.floor(r.y / TILE);
    const tx1 = Math.floor((r.x + r.w - 1) / TILE);
    const ty1 = Math.floor((r.y + r.h - 1) / TILE);
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const id = `${tx},${ty}`;
        if (entry.tiles.has(id)) continue;
        const x = tx * TILE;
        const y = ty * TILE;
        const w = Math.min(TILE, l.canvas.width - x);
        const h = Math.min(TILE, l.canvas.height - y);
        if (w <= 0 || h <= 0) continue;
        const data = ctx.getImageData(x, y, w, h);
        entry.tiles.set(id, { x, y, data });
        entry.bytes += data.data.byteLength;
      }
    }
  }

  /** The stroke is on the canvas: keep its undo entry, mark the layer dirty, schedule a commit. */
  endStroke(l: LiveLayer, changed: Rect | null): void {
    const entry = this.recording.get(l.key);
    this.recording.delete(l.key);
    if (!changed || !entry || !entry.tiles.size) return;
    const h = this.historyOf(l.sheetId);
    h.undo.push(entry);
    h.redo.length = 0;
    this.trimHistory(h);
    this.touched(l);
  }

  /** A stroke was abandoned (a second finger landed): put the backed-up tiles back. */
  cancelStroke(l: LiveLayer): void {
    const entry = this.recording.get(l.key);
    this.recording.delete(l.key);
    if (!entry) return;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return;
    for (const t of entry.tiles.values()) ctx.putImageData(t.data, t.x, t.y);
    this.bump();
  }

  /** Pixels changed outside a stroke session (fill): schedule the commit. */
  touched(l: LiveLayer): void {
    l.dirty = true;
    l.lastUsed = Date.now();
    this.scheduleCommit(l);
    this.bump();
  }

  private historyOf(sheetId: string): SheetHistory {
    let h = this.history.get(sheetId);
    if (!h) {
      h = { undo: [], redo: [] };
      this.history.set(sheetId, h);
    }
    return h;
  }

  private trimHistory(h: SheetHistory): void {
    let bytes = h.undo.reduce((s, e) => s + e.bytes, 0) + h.redo.reduce((s, e) => s + e.bytes, 0);
    while (h.undo.length && (h.undo.length > MAX_UNDO_STEPS || bytes > MAX_UNDO_BYTES)) {
      bytes -= h.undo.shift()!.bytes;
    }
  }

  /** Swaps an entry's tiles with the canvas (undo and redo are the same operation). */
  private async applyEntry(sheetId: string, entry: UndoEntry): Promise<UndoEntry | null> {
    const l = await this.activate(sheetId, entry.layerId);
    if (!l || !l.loaded) return null;
    const ctx = l.canvas.getContext('2d');
    if (!ctx) return null;
    const inverse: UndoEntry = { layerId: entry.layerId, tiles: new Map(), bytes: 0, at: Date.now() };
    for (const [id, t] of entry.tiles) {
      const now = ctx.getImageData(t.x, t.y, t.data.width, t.data.height);
      inverse.tiles.set(id, { x: t.x, y: t.y, data: now });
      inverse.bytes += now.data.byteLength;
      ctx.putImageData(t.data, t.x, t.y);
    }
    this.touched(l);
    return inverse;
  }

  /** Undoes the newest paint step of the sheet (activating its layer if needed). */
  async undo(sheetId: string): Promise<boolean> {
    // a move still floating is the newest step: undoing puts the pixels back
    if (this.float?.live.sheetId === sheetId) {
      this.cancelFloat();
      return true;
    }
    const h = this.historyOf(sheetId);
    const entry = h.undo.pop();
    if (!entry) return false;
    const inverse = await this.applyEntry(sheetId, entry);
    if (inverse) h.redo.push(inverse);
    this.bump();
    return true;
  }

  async redo(sheetId: string): Promise<boolean> {
    if (this.float) this.dropFloat();
    const h = this.historyOf(sheetId);
    const entry = h.redo.pop();
    if (!entry) return false;
    const inverse = await this.applyEntry(sheetId, entry);
    if (inverse) h.undo.push(inverse);
    this.bump();
    return true;
  }

  /** Forgets the paint history of a sheet (its layer structure changed under it). */
  clearHistory(sheetId: string, layerId?: string): void {
    const h = this.history.get(sheetId);
    if (!h) return;
    if (!layerId) {
      h.undo.length = 0;
      h.redo.length = 0;
    } else {
      h.undo = h.undo.filter((e) => e.layerId !== layerId);
      h.redo = h.redo.filter((e) => e.layerId !== layerId);
    }
    this.bump();
  }

  // ---- pixel selection -------------------------------------------------------------------

  /** The selection on a sheet, if it has one. */
  selection(sheetId: string): PixelSelection | null {
    return this.sel?.sheetId === sheetId ? this.sel : null;
  }

  /**
   * Selects what a closed outline (flat [x, y, ...], layer pixels) holds; fewer than three
   * points, or null, selects nothing. `aliased` gives the selection a hard pixel edge.
   */
  setSelection(sheetId: string, points: number[] | null, aliased = false): void {
    if (this.float) this.dropFloat();
    const sheet = this.sheet(sheetId);
    this.sel = sheet && points ? makeSelection(sheetId, points, aliased, sheet.props.pxW, sheet.props.pxH) : null;
    this.bump();
  }

  selectAll(sheetId: string): void {
    const sheet = this.sheet(sheetId);
    if (!sheet) return;
    const { pxW: w, pxH: h } = sheet.props;
    this.setSelection(sheetId, [0, 0, w, 0, w, h, 0, h], true);
  }

  /** What a stroke on the sheet has to stay inside (null: the whole layer). */
  strokeClip(sheetId: string): StrokeClip | null {
    const sel = this.selection(sheetId);
    if (!sel) return null;
    if (!sel.aliased) return { path: sel.path };
    const { x, y, w, h } = sel.bounds;
    const data = maskData(sel);
    return { path: sel.path, test: (px, py) => px >= x && py >= y && px < x + w && py < y + h && data[((py - y) * w + (px - x)) * 4 + 3]! >= 128 };
  }

  /** The same for a fill. */
  fillRegion(sheetId: string): FillRegion | null {
    const sel = this.selection(sheetId);
    return sel ? { ...sel.bounds, data: maskData(sel) } : null;
  }

  /** Rubs out what the selection holds (the floating pixels, when it has been lifted). */
  eraseSelection(l: LiveLayer): boolean {
    const f = this.float;
    if (f) {
      if (f.live !== l) return false;
      // the hole they left is the erasure; the pixels themselves are let go
      this.float = null;
      this.clearShown(f);
      this.endStroke(l, clipRect({ x: f.x0, y: f.y0, w: f.canvas.width, h: f.canvas.height }, l.canvas.width, l.canvas.height));
      f.canvas.width = 0;
      return true;
    }
    const sel = this.selection(l.sheetId);
    const rect = sel ? clipRect(sel.bounds, l.canvas.width, l.canvas.height) : null;
    const ctx = l.canvas.getContext('2d');
    if (!sel || !rect || !ctx || !l.loaded) return false;
    this.beginStroke(l)(rect);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(sel.mask, sel.bounds.x, sel.bounds.y);
    ctx.restore();
    this.endStroke(l, rect);
    return true;
  }

  // ---- floating pixels (the move tool) ----------------------------------------------------

  floating(): FloatingPixels | null {
    return this.float;
  }

  /**
   * Lifts the selected pixels of a layer (all of its pixels without a selection) so they can be
   * moved. The layer keeps the hole; the undo step is recorded when they land. False when there
   * is nothing to lift.
   */
  lift(l: LiveLayer): boolean {
    if (this.float) return this.float.live === l;
    const ctx = l.canvas.getContext('2d');
    if (!ctx || !l.loaded || !l.scratch) return false;
    const sel = this.selection(l.sheetId);
    const bounds = sel ? clipRect(sel.bounds, l.canvas.width, l.canvas.height) : contentBounds(l.canvas);
    if (!bounds) return false;
    const canvas = document.createElement('canvas');
    canvas.width = bounds.w;
    canvas.height = bounds.h;
    const fctx = canvas.getContext('2d');
    if (!fctx) return false;
    fctx.drawImage(l.canvas, bounds.x, bounds.y, bounds.w, bounds.h, 0, 0, bounds.w, bounds.h);
    if (sel) {
      fctx.globalCompositeOperation = 'destination-in';
      fctx.drawImage(sel.mask, sel.bounds.x - bounds.x, sel.bounds.y - bounds.y);
    }
    const backup = this.beginStroke(l);
    backup(bounds);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    if (sel) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(sel.mask, sel.bounds.x, sel.bounds.y);
    } else ctx.clearRect(bounds.x, bounds.y, bounds.w, bounds.h);
    ctx.restore();
    this.float = { live: l, canvas, x: bounds.x, y: bounds.y, x0: bounds.x, y0: bounds.y, at: Date.now(), backup, shown: null };
    this.showFloat();
    this.bump();
    return true;
  }

  /** Moves the floating pixels (and the selection with them) to a top-left position, whole pixels. */
  moveFloat(x: number, y: number): void {
    const f = this.float;
    if (!f) return;
    const nx = Math.round(x);
    const ny = Math.round(y);
    if (nx === f.x && ny === f.y) return;
    if (this.sel) this.sel = translated(this.sel, nx - f.x, ny - f.y);
    f.x = nx;
    f.y = ny;
    this.showFloat();
    this.bump();
  }

  /** The floating pixels land where they are: one undoable step. Back where they started, nothing happened. */
  dropFloat(): void {
    const f = this.float;
    if (!f) return;
    if (f.x === f.x0 && f.y === f.y0) return this.cancelFloat();
    this.float = null;
    this.clearShown(f);
    const l = f.live;
    const from = clipRect({ x: f.x0, y: f.y0, w: f.canvas.width, h: f.canvas.height }, l.canvas.width, l.canvas.height);
    const to = clipRect({ x: f.x, y: f.y, w: f.canvas.width, h: f.canvas.height }, l.canvas.width, l.canvas.height);
    const ctx = l.canvas.getContext('2d');
    if (to && ctx) {
      f.backup(to);
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(f.canvas, f.x, f.y);
      ctx.restore();
    }
    f.canvas.width = 0;
    this.endStroke(l, unionRect(from, to));
    this.bump();
  }

  /** Puts the floating pixels back where they were lifted from, as if never moved. */
  cancelFloat(): void {
    const f = this.float;
    if (!f) return;
    this.float = null;
    this.clearShown(f);
    if (this.sel && (f.x !== f.x0 || f.y !== f.y0)) this.sel = translated(this.sel, f.x0 - f.x, f.y0 - f.y);
    f.canvas.width = 0;
    this.cancelStroke(f.live);
  }

  private clearShown(f: FloatingPixels): void {
    const s = f.live.scratch;
    if (s && f.shown) s.getContext('2d')?.clearRect(f.shown.x, f.shown.y, f.shown.w, f.shown.h);
    f.shown = null;
  }

  /** The floating pixels are shown on the scratch canvas, which sits over the layer. */
  private showFloat(): void {
    const f = this.float;
    const s = f?.live.scratch;
    const sctx = s?.getContext('2d');
    if (!f || !s || !sctx) return;
    sctx.setTransform(1, 0, 0, 1, 0, 0);
    sctx.globalAlpha = 1;
    sctx.globalCompositeOperation = 'source-over';
    if (f.shown) sctx.clearRect(f.shown.x, f.shown.y, f.shown.w, f.shown.h);
    sctx.drawImage(f.canvas, f.x, f.y);
    f.shown = { x: f.x, y: f.y, w: f.canvas.width, h: f.canvas.height };
    s.style.opacity = '';
  }

  // ---- commits ---------------------------------------------------------------------------

  private scheduleCommit(l: LiveLayer): void {
    const t = this.timers.get(l.key);
    if (t) clearTimeout(t);
    this.timers.set(
      l.key,
      setTimeout(() => {
        this.timers.delete(l.key);
        void this.commit(l);
      }, COMMIT_DELAY_MS),
    );
  }

  /** Encodes the layer and points the document at the new asset. Safe to call any time. */
  commit(l: LiveLayer): Promise<void> {
    const t = this.timers.get(l.key);
    if (t) clearTimeout(t);
    this.timers.delete(l.key);
    const running = this.committing.get(l.key);
    if (running) return running;
    if (!l.dirty || !l.loaded) return Promise.resolve();
    if (this.float?.live === l) {
      // pixels are floating over a hole in this layer: save it once they have landed
      this.scheduleCommit(l);
      return Promise.resolve();
    }
    const p = (async () => {
      // toBlob snapshots the bitmap synchronously; strokes landing meanwhile mark it dirty again
      l.dirty = false;
      try {
        const meta = await storeCanvasAsset(l.canvas, this.board.boardId, `sheet-${l.layerId.slice(0, 8)}.png`);
        if (!this.live.has(l.key)) return;
        l.assetId = meta.id;
        if (this.layerDef(l.sheetId, l.layerId)) {
          this.board.setSheetLayerAsset(l.sheetId, l.layerId, meta.id);
          if (this.board.remote) void enqueueUpload(this.board.boardId, meta.id);
        }
      } catch (e) {
        console.warn('[paint] layer not saved, will retry', e);
        l.dirty = true;
      }
    })().finally(() => {
      this.committing.delete(l.key);
      if (l.dirty && this.live.has(l.key)) this.scheduleCommit(l);
      else if (l.retired) this.pruneRetired();
      this.bump();
    });
    this.committing.set(l.key, p);
    return p;
  }

  /** Commits every dirty layer now (the tab is going to the background). */
  flush(): Promise<void> {
    if (this.float) this.dropFloat();
    return Promise.all([...this.live.values()].filter((l) => l.dirty).map((l) => this.commit(l))).then(() => undefined);
  }

  /** Is a commit or a load in flight for the sheet (the panel shows a saving hint)? */
  busy(sheetId: string): boolean {
    for (const l of this.live.values()) if (l.sheetId === sheetId && (this.committing.has(l.key) || l.dirty || !l.loaded)) return true;
    return false;
  }

  destroy(): void {
    this.unsubscribe();
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    for (const l of this.live.values()) {
      l.canvas.width = 0;
      l.canvas.height = 0;
    }
    this.live.clear();
    this.history.clear();
    this.listeners.clear();
    this.sel = null;
    this.float = null;
  }
}

// ---- selection helpers -------------------------------------------------------------------

function outlinePath(points: number[]): Path2D {
  const path = new Path2D();
  for (let i = 0; i + 1 < points.length; i += 2) {
    if (i === 0) path.moveTo(points[i]!, points[i + 1]!);
    else path.lineTo(points[i]!, points[i + 1]!);
  }
  path.closePath();
  return path;
}

function makeSelection(sheetId: string, points: number[], aliased: boolean, w: number, h: number): PixelSelection | null {
  if (points.length < 6) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i + 1 < points.length; i += 2) {
    x0 = Math.min(x0, points[i]!);
    x1 = Math.max(x1, points[i]!);
    y0 = Math.min(y0, points[i + 1]!);
    y1 = Math.max(y1, points[i + 1]!);
  }
  const bounds = clipRect({ x: Math.floor(x0), y: Math.floor(y0), w: Math.ceil(x1) - Math.floor(x0), h: Math.ceil(y1) - Math.floor(y0) }, w, h);
  if (!bounds) return null;
  const path = outlinePath(points);
  const mask = document.createElement('canvas');
  mask.width = bounds.w;
  mask.height = bounds.h;
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.translate(-bounds.x, -bounds.y);
  ctx.fillStyle = '#000';
  ctx.fill(path);
  let data: Uint8ClampedArray | null = null;
  if (aliased) {
    // every pixel in or out: the ones the outline covers at least half of
    const img = ctx.getImageData(0, 0, bounds.w, bounds.h);
    for (let i = 3; i < img.data.length; i += 4) img.data[i] = img.data[i]! >= 128 ? 255 : 0;
    ctx.putImageData(img, 0, 0);
    data = img.data;
  }
  return { sheetId, points, path, bounds, mask, aliased, data };
}

function maskData(sel: PixelSelection): Uint8ClampedArray {
  if (!sel.data) {
    const ctx = sel.mask.getContext('2d', { willReadFrequently: true });
    sel.data = ctx ? ctx.getImageData(0, 0, sel.bounds.w, sel.bounds.h).data : new Uint8ClampedArray(sel.bounds.w * sel.bounds.h * 4);
  }
  return sel.data;
}

/** The same selection somewhere else (the mask is shared: it never changes). */
function translated(sel: PixelSelection, dx: number, dy: number): PixelSelection {
  const points = sel.points.map((v, i) => v + (i % 2 ? dy : dx));
  return { ...sel, points, path: outlinePath(points), bounds: { ...sel.bounds, x: sel.bounds.x + dx, y: sel.bounds.y + dy } };
}

/** The box around every pixel that is not fully transparent, read a strip at a time. */
function contentBounds(canvas: HTMLCanvasElement): Rect | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const w = canvas.width;
  const h = canvas.height;
  const STRIP = 256;
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let sy = 0; sy < h; sy += STRIP) {
    const sh = Math.min(STRIP, h - sy);
    const d = ctx.getImageData(0, sy, w, sh).data;
    for (let y = 0; y < sh; y++) {
      const row = y * w * 4 + 3;
      let l = 0;
      while (l < w && d[row + l * 4] === 0) l++;
      if (l === w) continue;
      let r = w - 1;
      while (d[row + r * 4] === 0) r--;
      if (l < x0) x0 = l;
      if (r > x1) x1 = r;
      if (sy + y < y0) y0 = sy + y;
      y1 = sy + y;
    }
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

// ---- one store per document --------------------------------------------------------------

const stores = new WeakMap<BoardDoc, RasterStore>();

export function rasterStore(board: BoardDoc): RasterStore {
  let s = stores.get(board);
  if (!s) {
    s = new RasterStore(board);
    stores.set(board, s);
  }
  return s;
}
